import React, { useState, useEffect, useCallback } from 'react';
import { Modal, Input, Card, Spin, Button, Empty, message, Avatar } from 'antd';
import { SearchOutlined, UserOutlined, CheckOutlined } from '@ant-design/icons';
import { FaUserMd } from 'react-icons/fa';
import drEasyApi from '../../Api/Customapi';
import { getImg } from '../../Api/getImg';

const ReassignSpecialistModal = ({ visible, onCancel, session, isMobile, onSuccess }) => {
  const [specialists, setSpecialists] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [selected, setSelected] = useState(null);
  const [pageNumber] = useState(1);
  const [pageSize] = useState(20);
  const [totalCount, setTotalCount] = useState(0);

  const fetchSpecialists = useCallback(async () => {
    setLoading(true);
    try {
      const params = { PageNumber: pageNumber, PageSize: pageSize };
      if (searchTerm.trim()) params.NameSearch = searchTerm;

      const response = await drEasyApi.get('/api/Centers/getMySpecialists', { params });
      setSpecialists(response.data?.specialists || []);
      setTotalCount(response.data?.totalCount || 0);
    } catch (error) {
      message.error('Failed to fetch specialists');
      setSpecialists([]);
    } finally {
      setLoading(false);
    }
  }, [pageNumber, pageSize, searchTerm]);

  useEffect(() => {
    if (visible) {
      setSelected(null);
      setSearchTerm('');
      fetchSpecialists();
    }
  }, [visible]);

  useEffect(() => {
    if (visible) fetchSpecialists();
  }, [searchTerm, visible]);

  const handleReassign = async () => {
    if (!selected) return message.warning('Please select a specialist');

    setSubmitting(true);
    try {
      await drEasyApi.put('/api/PatientSessions/ReAssignSpecialist', null, {
        params: {
          SessionId: session.sessionId,
          SpecialistID: selected.specialistId,
        },
      });
      message.success(`Session reassigned to ${selected.name} successfully`);
      onSuccess();
    } catch (error) {
      message.error(
        error.response?.data?.[0] || 'Failed to reassign specialist'
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      title={
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
            <FaUserMd className="text-white text-sm" />
          </div>
          <span>Reassign Specialist</span>
        </div>
      }
      open={visible}
      onCancel={onCancel}
      width={isMobile ? '95vw' : 600}
      centered={isMobile}
      footer={[
        <Button key="cancel" onClick={onCancel}>Cancel</Button>,
        <Button
          key="reassign"
          type=""
          onClick={handleReassign}
          loading={submitting}
          disabled={!selected}
          className="rounded-lg bg-primary hover:bg-primary_hover text-white cursor-pointer border-none"
        >
          Reassign
        </Button>,
      ]}
    >
      {/* Current session info */}
      {session && (
        <div className="mb-4 p-3 bg-primary/20 border border-primary rounded-lg text-sm space-y-1">
          <div><span className="font-semibold">Patient:</span> {session.patientName}</div>
          <div><span className="font-semibold">Current Specialist:</span> {session.specialistName}</div>
        </div>
      )}

      <Input
        placeholder="Search by specialist name"
        prefix={<SearchOutlined className="text-gray-400" />}
        value={searchTerm}
        onChange={(e) => setSearchTerm(e.target.value)}
        allowClear
        size={isMobile ? 'middle' : 'large'}
        className="rounded-xl mb-4"
      />

      <div className="text-sm font-semibold mb-3 text-gray-700">
        Select New Specialist ({totalCount} found)
      </div>

      <Spin spinning={loading}>
        {specialists.length > 0 ? (
          <div className="max-h-72 overflow-y-auto space-y-2">
            {specialists.map((specialist) => (
              <Card
                key={specialist.specialistId}
                className={`cursor-pointer rounded-lg transition-all border-2 ${
                  selected?.specialistId === specialist.specialistId
                    ? 'border-primary/70 bg-primary/10 shadow-md'
                    : 'border-gray-200 hover:border-primary'
                }`}
                onClick={() =>
                  setSelected(
                    selected?.specialistId === specialist.specialistId
                      ? null
                      : specialist
                  )
                }
              >
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Avatar
                      size={40}
                      src={specialist.photoId ? getImg(specialist.photoId) : null}
                      icon={!specialist.photoId ? <UserOutlined /> : null}
                      className="bg-gradient-to-r from-primary/70 to-primary flex-shrink-0"
                    />
                    <div>
                      <div className="font-semibold text-gray-800">{specialist.name}</div>
                      {specialist.specialization && (
                        <div className="text-xs text-gray-500">{specialist.specialization}</div>
                      )}
                    </div>
                  </div>
                  {selected?.specialistId === specialist.specialistId && (
                    <div className="w-6 h-6 bg-primary rounded-full flex items-center justify-center flex-shrink-0">
                      <CheckOutlined className="text-white text-xs" />
                    </div>
                  )}
                </div>
              </Card>
            ))}
          </div>
        ) : (
          !loading && <Empty description="No specialists found" className="py-6" />
        )}
      </Spin>
    </Modal>
  );
};

export default ReassignSpecialistModal;